import { useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { displayName, initials, isExternalVendor } from '../people';
import { confirmDialog } from '../confirm';
import { humanDay } from '../when';
import DateField from './DateField';
import CoverPicker, { dayFromMs, endOfDayMs } from './CoverPicker';
import {
  effectiveRole,
  roleIsSetHere,
  PROJECT_ROLES,
  ROLE_LABEL,
  ROLE_BLURB,
  type ProjectRole,
} from '../permissions';
import {
  accessChangeWarning,
  accessSummary,
  boardsForPerson,
  type Project,
} from '../projects';
import { accessReason, accessVia, removalProblem, TEAMS_ARE_CONNECTED } from '../groups';
import Icon from './Icon';

/**
 * ONE PERSON'S BOARDS AND TEAMS, INSIDE THEIR OWN PANEL.
 *
 * IT WAS A TAB OF ITS OWN, and that was the mistake. Managing a person was
 * split across three screens — their name and role on People, their boards on
 * Access, their teams on Teams — so setting somebody up meant visiting all
 * three and remembering which held what. Every one of those screens started by
 * asking WHICH PERSON, which is the same question answered three times.
 *
 * A person is one subject. Everything about them belongs in one place, and the
 * Teams screen keeps the other direction — one team, its people, its boards —
 * because that is a different subject rather than the same one split up.
 *
 * WHAT SURVIVED FROM THE VERSION BEFORE THIS. Boards run DOWN the page rather
 * than across: measured at twenty boards, a grid was three screens of sideways
 * scrolling and 240 tick boxes. And the boards they can open come first,
 * because a new starter is on two out of twenty and those two are what is
 * being checked.
 */
export default function PersonAccess({
  email: person,
  report,
}: {
  /** Whose access this is. The panel belongs to a person, not the other way round. */
  email: string;
  /** Reports a refused write on the parent screen — see Admin. */
  report: (p: Promise<unknown>) => void;
}) {
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const legacyRoles = useApp((s) => s.legacyRoles);
  const projectRoles = useApp((s) => s.projectRoles);
  const projects = useApp((s) => s.projects);
  const groups = useApp((s) => s.groups);
  const groupsOf = useApp((s) => s.groupsOf);
  const profiles = useApp((s) => s.profiles);
  const addToProject = useApp((s) => s.addToProject);
  const removeFromProject = useApp((s) => s.removeFromProject);
  const setRoleOn = useApp((s) => s.setRoleOn);
  const clearRoleOn = useApp((s) => s.clearRoleOn);

  const [filter, setFilter] = useState('');
  const [warning, setWarning] = useState('');
  const setInGroup = useApp((s) => s.setInGroup);
  const isAdmin = access.admins.includes(person);
  /* Whether the person LOOKING is an admin — the one who may reach into somebody
     else's notification switches. Distinct from `isAdmin`, which is about the
     person being looked at. */
  const user = useApp((s) => s.user);
  const viewerIsAdmin = access.admins
    .map((a) => a.toLowerCase())
    .includes((user?.email ?? '').toLowerCase());

  const { on, off } = useMemo(
    () => boardsForPerson({ projects, email: person, isAdmin, filter, groupsOf }),
    [projects, person, isAdmin, filter, groupsOf]
  );

  const roleHere = (p: Project) =>
    effectiveRole({
      email: person,
      admins: access.admins,
      defaults: roles,
      on: projectRoles,
      legacy: legacyRoles,
      projectId: p.id,
    });

  /** One board's row: whether they are on it, and what they may do there. */
  const row = (p: Project, isOn: boolean) => {
    const here = roleHere(p);
    const chosen = roleIsSetHere(projectRoles, person, p.id);
    return (
      <div key={p.id} className={`pa-row ${isOn ? 'is-on' : ''}`}>
        <span className="project-badge">{p.key}</span>
        <div className="pa-board">
          <span className="pa-name">{p.name}</span>
          {/* WHERE THE ACCESS COMES FROM, on every row that has any. A tick
              that does not say is what made the old screen confusing: somebody
              appears to be on a board, Remove is pressed, and nothing happens
              because their access was never the tick. */}
          {isOn && (
            <span className="pa-note">
              {accessReason(accessVia(p, person, isAdmin, groupsOf), groups)}
            </span>
          )}
        </div>

        {isAdmin ? (
          /* Allowed by rank, not by any list. A control here would appear to
             work and change nothing about what they can reach. */
          <span className="pa-fixed" title="Admins can open every board and do anything on it">
            <Icon name="check" size={13} /> Admin
          </span>
        ) : (
          <>
            {isOn && (
              <select
                className={`input pa-role ${chosen ? 'is-set' : ''}`}
                value={here === 'admin' ? 'manager' : here}
                aria-label={`What ${displayName(person, profiles)} may do on ${p.name}`}
                title={chosen ? `Chosen for ${p.name}` : ROLE_BLURB[here]}
                onChange={(e) => {
                  const v = e.target.value;
                  setWarning('');
                  report(
                    v === 'default' ? clearRoleOn(person, p.id) : setRoleOn(person, p.id, v as ProjectRole)
                  );
                }}
              >
                {PROJECT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                    {/* Two words apart on purpose: chosen for this board, or
                        following a default set on the Roles tab. Without the
                        difference somebody changes a default believing they
                        changed one board. */}
                    {!chosen && r === here ? ' — their default' : ''}
                  </option>
                ))}
                {/* THE VENDOR TIER sits apart, below the internal ladder, under its
                    own "External access" group — the Monday/Asana guest pattern, so
                    an outside supplier is never granted by mistake from the normal
                    list (B-03). Granting it writes a board-scoped `vendor` role that
                    the RLS reads to gate this board and bar every other; the picker
                    only offers it. The blurb is in the control's title tooltip. */}
                <optgroup label="External access">
                  <option value="vendor">{ROLE_LABEL.vendor}</option>
                </optgroup>
                {chosen && <option value="default">Follow their default</option>}
              </select>
            )}
            <button
              className={`btn btn-small ${isOn ? '' : 'btn-primary'}`}
              onClick={async () => {
                /* REFUSED RATHER THAN RUN, where running would do nothing.
                   Somebody on a board through a team is not on its named list,
                   so removing them from it removes nothing — the button would
                   report success and change the world not at all. Both real
                   ways out are named instead. */
                const viaTeam = isOn ? removalProblem(p, person, groupsOf, groups) : '';
                if (viaTeam) return setWarning(viaTeam);
                if (isOn) {
                  setWarning(accessChangeWarning(p, false));
                  report(removeFromProject(p.id, person));
                  return;
                }
                /* THE NUDGE, not a force (B-08). An External / Vendor person is
                   added with the Vendor access tier by default — the safe grant
                   for an outside party — but it is confirmed once, and cancelling
                   leaves them off the board so a different role can be chosen
                   instead. Their company role (who they are) suggests the access
                   tier (what they can do); the two stay independent. */
                if (isExternalVendor(person, profiles)) {
                  const ok = await confirmDialog({
                    title: `Add ${displayName(person, profiles)} to ${p.name} with Vendor access?`,
                    body: (
                      <>
                        Their company role is <b>External / Vendor</b>. Vendor access is a work
                        surface only — they can see and update this board’s cards and post updates,
                        but not its settings, finances, sign-offs, decisions, or any other board.
                      </>
                    ),
                    confirmLabel: 'Add with Vendor access',
                    tone: 'caution',
                  });
                  if (!ok) return;
                  setWarning(accessChangeWarning(p, true));
                  report(addToProject(p.id, person, 'vendor'));
                  return;
                }
                setWarning(accessChangeWarning(p, true));
                report(addToProject(p.id, person, 'reviewer'));
              }}
            >
              {isOn ? 'Remove' : 'Add'}
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="stack pa-block">
        <span className="pa-title">
          Boards they can open
          <span className="hint">{accessSummary(on.length, projects.length, isAdmin)}</span>
        </span>

        {/* Offered once there are enough boards for finding one to be work.
            Below that it is a box that never has anything to do. */}
        {projects.length > 6 && (
          <input
            className="input pa-find"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Find a board"
          />
        )}

        {warning && (
          <p className="grid-warning" role="alert">
            {warning}
          </p>
        )}

        {on.length > 0 && <div className="pa-list">{on.map((p) => row(p, true))}</div>}
        {off.length > 0 && (
          <>
            <div className="pa-heading">Cannot open</div>
            <div className="pa-list">{off.map((p) => row(p, false))}</div>
          </>
        )}
        {on.length === 0 && off.length === 0 && <p className="hint">No board matches “{filter}”.</p>}
      </div>

      {/* TEAMS, ON THE PERSON — the other half of what used to be a separate
          screen. Access through a team is the commonest kind once teams exist,
          so "which teams are they in" belongs beside "which boards can they
          open" rather than one tab away. */}
      {/* HIDDEN with the Teams tab — ticking one wrote nothing and said it had.
          src/groups.ts is the only switch. */}
      {TEAMS_ARE_CONNECTED && (
      <div className="stack pa-block">
        <span className="pa-title">
          Teams they are in
          <span className="hint">
            A team opens every board it is named on — see the Teams tab for which.
          </span>
        </span>
        {groups.length === 0 ? (
          <p className="hint">No teams have been made yet.</p>
        ) : (
          <div className="pa-teams">
            {groups.map((g) => {
              const isIn = (groupsOf[person.toLowerCase()] ?? []).includes(g.id);
              const opens = projects.filter((p) => (p.groups ?? []).includes(g.id)).length;
              return (
                <label key={g.id} className="grp-row">
                  <input
                    type="checkbox"
                    checked={isIn}
                    onChange={(e) => report(setInGroup(person, g.id, e.target.checked))}
                  />
                  <span className="grp-name">{g.name}</span>
                  {/* What joining actually grants, so a tick is not a guess. */}
                  <span className="grp-sub">
                    {opens} {opens === 1 ? 'board' : 'boards'}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* SIGN-OFF COVER, ARRANGED BY AN ADMIN. For when a person is unreachable
          and never set their own cover — someone who left suddenly, or is on leave
          (0130). The admin names who signs for them and why; the person sees and
          can change it on their own "When I'm away" screen. Only a regular member,
          only to an admin. */}
      {viewerIsAdmin && !isAdmin && <AdminCover email={person} />}

      {/* NOTIFICATIONS AN ADMIN CAN INSIST ON. A person can switch their own
          sign-off notices off; a manager watching them miss the approvals they
          owe can turn one back on and lock it, so it cannot be switched off again
          (0126). Only for a regular member, and only to an admin. */}
      {viewerIsAdmin && !isAdmin && <AdminNotifyOverride email={person} report={report} />}

      <p className="hint pa-foot">
        A role marked <em>their default</em> is inherited from the Roles tab and follows any change
        made there. Choosing one pins it to this board alone, and a manager here can be a reviewer
        somewhere else.
      </p>
    </>
  );
}

/**
 * AN ADMIN TURNS A SIGN-OFF NOTICE BACK ON FOR A MEMBER — the accountability
 * override (B, 28 Sep). Only the action-required kinds are here: those are the
 * ones whose muting has a cost worth a manager's attention. Turning one on locks
 * it (the member cannot switch it off again); unlocking returns them to their own
 * choice. Every change is written to the trail by the store action.
 */
const OVERRIDE_KINDS: { kind: string; name: string }[] = [
  { kind: 'signoff_requested', name: 'A sign-off is requested of them' },
  { kind: 'signoff_decided', name: 'A decision on something they raised' },
];

function AdminNotifyOverride({
  email,
  report,
}: {
  email: string;
  report: (p: Promise<unknown>) => void;
}) {
  const load = useApp((s) => s.notificationSwitches);
  const adminSet = useApp((s) => s.adminSetKind);
  const [state, setState] = useState<Record<string, { muted: boolean; lockedBy: string }>>({});
  const [busy, setBusy] = useState('');

  const refresh = () => {
    void load(email).then((rows) => {
      const m: Record<string, { muted: boolean; lockedBy: string }> = {};
      for (const r of rows) m[r.kind] = { muted: r.muted, lockedBy: r.lockedBy };
      setState(m);
    });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(refresh, [email]);

  const act = async (kind: string, on: boolean, lock: boolean) => {
    setBusy(kind);
    const p = adminSet(email, kind, on, lock);
    report(p);
    await p;
    setBusy('');
    refresh();
  };

  return (
    <div className="stack pa-block">
      <span className="pa-title">
        Notifications
        <span className="hint">
          Turn a sign-off notice back on for someone who has switched it off, and lock it so it
          stays on.
        </span>
      </span>
      <div className="pa-teams">
        {OVERRIDE_KINDS.map((k) => {
          const st = state[k.kind];
          const lockedOn = !!st?.lockedBy;
          const off = !!st?.muted && !lockedOn;
          const label = lockedOn ? 'Locked on' : off ? 'Switched off' : 'On';
          return (
            <div key={k.kind} className="grp-row">
              <span className="grp-name">{k.name}</span>
              <span className="grp-sub">{label}</span>
              {lockedOn ? (
                <button className="link" disabled={busy === k.kind} onClick={() => void act(k.kind, false, false)}>
                  Unlock
                </button>
              ) : (
                <button className="link" disabled={busy === k.kind} onClick={() => void act(k.kind, true, true)}>
                  {off ? 'Turn on and lock' : 'Lock on'}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * AN ADMIN ARRANGES COVER FOR SOMEONE — sign-offs R1, increment 2 (0130, mock 5).
 *
 * For a person who is unreachable and never set their own cover: someone who left
 * suddenly, or is on leave. The admin names who signs for them and MUST say why
 * (the reason is written to the trail and emailed to the covered person; the
 * database enforces it, 0130). Cover spans every board the person is on, like the
 * self-service screen. The person sees it on their own "When I'm away" and can
 * change or remove it; both of them, and the nominee, are notified (0132).
 *
 * A cover the person set THEMSELVES is shown read-only here — the admin can replace
 * it (which makes it admin-arranged, so a reason is required) or turn it off.
 */
function AdminCover({ email: person }: { email: string }) {
  const covers = useApp((s) => s.signoffCover);
  const setCover = useApp((s) => s.setCover);
  const clearCover = useApp((s) => s.clearCover);
  const profiles = useApp((s) => s.profiles);
  const access = useApp((s) => s.access);

  const who = person.toLowerCase();
  const mine = covers.find((c) => c.delegator === who);
  const name = displayName(person, profiles);
  const first = name.split(' ')[0] || name;
  const adminSet = !!mine?.setByAdmin;
  const nomineeName = mine ? displayName(mine.delegate, profiles) : '';
  const nomineePosition = mine ? profiles[mine.delegate]?.title?.trim() ?? '' : '';
  const setByName = mine ? displayName(mine.setBy, profiles) : '';

  const [arranging, setArranging] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  /* The whole arrange form counts as "inside" for the picker's click-away, so
     clicking the Reason field beside the picker does not cancel the form. */
  const arrangeBox = useRef<HTMLDivElement>(null);

  const openArrange = () => {
    setReason(mine?.reason ?? '');
    setProblem('');
    setArranging(true);
  };

  const arrange = async (delegate: string) => {
    if (!reason.trim()) {
      setProblem('Give a reason for arranging this cover.');
      return;
    }
    setBusy(true);
    setProblem('');
    // active:true — arranging turns cover ON, even when it replaces a paused one.
    const msg = await setCover({
      delegator: person,
      delegate,
      reason: reason.trim(),
      until: mine?.until ?? null,
      active: true,
    });
    setBusy(false);
    if (msg) {
      setProblem(msg);
      return;
    }
    setArranging(false);
  };

  const changeUntil = async (iso: string) => {
    if (!mine) return;
    setBusy(true);
    setProblem('');
    // Preserve the on/off state (no `active`) and the reason the cover was set with.
    const msg = await setCover({
      delegator: person,
      delegate: mine.delegate,
      until: endOfDayMs(iso),
      reason: mine.reason,
    });
    setBusy(false);
    if (msg) setProblem(msg);
  };

  const turnOff = async () => {
    setBusy(true);
    setProblem('');
    const msg = await clearCover(person);
    setBusy(false);
    if (msg) setProblem(msg);
  };

  const turnOn = async () => {
    if (!mine) return;
    if (!mine.reason.trim()) {
      // A self-set cover has no admin reason; turning it on as the admin needs one.
      openArrange();
      return;
    }
    setBusy(true);
    setProblem('');
    const msg = await setCover({
      delegator: person,
      delegate: mine.delegate,
      until: mine.until ?? null,
      reason: mine.reason,
      active: true,
    });
    setBusy(false);
    if (msg) setProblem(msg);
  };

  return (
    <div className="stack pa-block">
      <span className="pa-title">
        Sign-off cover
        <span className="hint">
          Set who can sign {first}’s sign-offs while they’re unreachable — someone who left suddenly,
          or is on leave without having set this up themselves. {first} sees this on their own account
          and can change or remove it.
        </span>
      </span>

      {problem && (
        <p className="cover-problem" role="alert">
          {problem}
        </p>
      )}

      {arranging ? (
        <div className="cover-arrange" ref={arrangeBox}>
          <label className="cover-field">
            <span className="cover-lbl">
              Reason <span className="req">*</span>
            </span>
            <input
              className="input"
              value={reason}
              autoFocus
              placeholder={`Why ${first} needs cover — e.g. on leave until the 20th`}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <CoverPicker
            me={who}
            label={`Who signs for ${first}`}
            emails={access.emails}
            profiles={profiles}
            busy={busy}
            onPick={(e) => void arrange(e)}
            onCancel={() => setArranging(false)}
            boundary={arrangeBox}
          />
        </div>
      ) : !mine ? (
        <div className="cover-empty">
          <span className="cover-empty-ic" aria-hidden="true">
            <Icon name="userPlus" size={22} />
          </span>
          <p className="cover-empty-h">No cover set — {first} answers their own sign-offs</p>
          <p className="cover-empty-sub">
            Arrange this when {first} is away and hasn’t set it up, so any pending or new sign-off can
            be signed on their behalf until they’re back.
          </p>
          <button className="btn btn-primary" onClick={openArrange}>
            Set cover for {first}
          </button>
        </div>
      ) : (
        <div className={`cover-card ${mine.active ? 'is-on' : 'is-off'}`}>
          <div className="cover-card-head">
            <span className="cover-av" aria-hidden="true">
              {initials(mine.delegate, profiles)}
            </span>
            <div className="cover-who">
              <div className="cover-who-name">{nomineeName}</div>
              <div className="cover-who-sub">
                {nomineePosition ? `${nomineePosition} · ` : ''}covers {first} across every board they
                are on
              </div>
            </div>
            {adminSet ? (
              <span className="cover-pill" title={`Arranged by ${setByName}`}>
                <Icon name="shield" size={13} /> Arranged by {setByName}
              </span>
            ) : (
              <span className="cover-pill cover-pill-self">Set by {first}</span>
            )}
          </div>

          {adminSet && mine.reason && <p className="cover-reason-shown">“{mine.reason}”</p>}

          {adminSet ? (
            <div className="cover-grid">
              <div className="cover-field">
                <span className="cover-lbl">Who signs for {first}</span>
                <span className="sr-signer">
                  <span className="sr-signer-av" aria-hidden="true">
                    {initials(mine.delegate, profiles)}
                  </span>
                  <span className="sr-signer-name">{nomineeName}</span>
                  {nomineePosition && <span className="sr-signer-sub">{nomineePosition}</span>}
                </span>
              </div>

              <div className="cover-field">
                <span className="cover-lbl">Until</span>
                <DateField
                  ariaLabel={`Cover for ${first} ends on`}
                  variant="form"
                  value={dayFromMs(mine.until)}
                  placeholder="No end date"
                  min={dayFromMs(Date.now())}
                  onChange={(iso) => void changeUntil(iso)}
                />
              </div>

              <div className="cover-field cover-field-toggle">
                <span className="cover-lbl">Cover</span>
                <div className="cover-switch-row">
                  <button
                    className={`switch ${mine.active ? 'on' : ''}`}
                    role="switch"
                    aria-checked={mine.active}
                    aria-label={`Cover for ${first} on`}
                    disabled={busy}
                    onClick={() => void (mine.active ? turnOff() : turnOn())}
                  >
                    <span className="switch-knob" />
                  </button>
                  <span className="cover-switch-state">{mine.active ? 'On' : 'Off'}</span>
                </div>
              </div>
            </div>
          ) : null}

          {mine.active ? (
            <div className="cover-banner cover-banner-on">
              <span className="cover-dot" aria-hidden="true" />
              <span>
                On now — {nomineeName} can sign {name}’s pending and future sign-offs across every
                board they are on{mine.until ? ` until ${humanDay(dayFromMs(mine.until))}` : ''}.
              </span>
            </div>
          ) : (
            <div className="cover-banner cover-banner-off">
              <span>
                Off — {name} answers their own sign-offs. Turn it on to let {nomineeName} sign for them
                again.
              </span>
            </div>
          )}

          <div className="cover-admin-actions">
            <button className="btn btn-small" disabled={busy} onClick={openArrange}>
              {adminSet ? 'Change' : 'Replace'}
            </button>
            {mine.active && (
              <button className="btn btn-small" disabled={busy} onClick={() => void turnOff()}>
                Turn off
              </button>
            )}
          </div>
        </div>
      )}

      <p className="hint pa-foot">
        Cover spans every board {first} is on. Every signature the nominee makes is stamped “on behalf
        of {name}”, so the trail always shows who actually signed.
      </p>
    </div>
  );
}
